import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import Popup from 'semantic-ui-react/dist/commonjs/modules/Popup';

import { AppDispatch } from '../../store.type';
import { BankingName, ModalContainer } from './styles';
import { CommonButton } from '@Components';
import {
  CUSTOM_ALERT_INVALID_BANK_NAME,
  CustomInfoAlertMessageStateType,
  CustomInfoAlterMessageProps,
  getContentForAlertPopUp,
  transformBankDetails
} from './index';
import { activateLoader, disableLoader } from '@Redux/actions/ui-trigger';
import { compileTemplateNewWithStyling, copyHtmlCommand } from '../../utils/library';
import { CLOSE_NEW_ICON, COPY_ICON, MAIL_ICON } from '@Images';
import { getCustomAlertErrorDefaultValues } from './methods';

const CustomInfoAlertMessage = ({
  contentId = '',
  contentType = '',
  primaryCtaOnClick,
  secondaryCtaOnClick,
  isCMSOriented = true,
  heading,
  icon,
  secondaryCtaText,
  primaryCtaText,
  desc,
  shareMessage,
  shouldShowShareCTAs = false,
  beneValidationObject = {},
  values = {},
  bankNameInvalid = false,
  beneficiaryBankName,
  displayBeneName,
  showConsent = false,
  isBankVerificationUnavailable = false,
  isTransactionFlow = false,
  showCloseIcon = false,
  onClose
}: CustomInfoAlterMessageProps): React.ReactElement<CustomInfoAlterMessageProps> | null => {
  const getAlertPayload = (defaultValues: typeof CUSTOM_ALERT_INVALID_BANK_NAME) => ({
    heading: heading || defaultValues.heading,
    icon: icon || defaultValues.icon,
    desc: desc || defaultValues.desc,
    secondaryCtaText: secondaryCtaText || defaultValues.secondaryCtaText,
    primaryCtaText: primaryCtaText || defaultValues.primaryCtaText,
    shareMessage: shareMessage || defaultValues.shareMessage
  });

  const defaultPayloadForConstData = getAlertPayload(
    getCustomAlertErrorDefaultValues(
      bankNameInvalid,
      displayBeneName,
      isBankVerificationUnavailable
    )
  );
  const NAME = <BankingName>{beneficiaryBankName}</BankingName>;
  const showBankNameinEmail = bankNameInvalid
    ? displayBeneName
      ? `Banking name: '${beneficiaryBankName}' <br/>`
      : ''
    : '';

  // Setting up the initial state CUSTOM_ALERT_INVALID_BANK_NAME from the schema to show the messages in the modal
  const getInitialState = (): CustomInfoAlertMessageStateType =>
    isCMSOriented
      ? getCustomAlertErrorDefaultValues(
          bankNameInvalid,
          displayBeneName,
          isBankVerificationUnavailable
        )
      : defaultPayloadForConstData;

  const [state, setState] = useState<CustomInfoAlertMessageStateType>(getInitialState);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const dispatch = useDispatch<AppDispatch>();
  const shouldShowSecondaryCTA =
    (isTransactionFlow && !isEmpty(state.transactionFlowLabel?.secondaryCTA)) || !isTransactionFlow;

  const handleShareCTAClick = (isEmailMessage: boolean) => {
    if (isEmailMessage) {
      return transformBankDetails(
        beneValidationObject,
        values,
        state.shareMessage,
        showBankNameinEmail,
        true
      );
    }
    return copyHtmlCommand(
      transformBankDetails(beneValidationObject, values, state.shareMessage, showBankNameinEmail)
    );
  };

  useEffect(() => {
    if (isCMSOriented) {
      (async () => {
        dispatch(activateLoader());
        await getContentForAlertPopUp(
          contentId,
          contentType,
          setState,
          setIsLoading,
          bankNameInvalid,
          displayBeneName
        );
        dispatch(disableLoader());
      })();
    }
    // eslint-disable-next-line
  }, [contentId, contentType]);

  if (isLoading) return null;
  return (
    <ModalContainer>
      {showCloseIcon && (
        <div className="close-wrapper">
          <img
            className="close-icon"
            src={CLOSE_NEW_ICON}
            alt="close"
            onClick={() => {
              onClose && onClose();
            }}
          />
        </div>
      )}
      <div className="content-container">
        <img src={state.icon} alt="alert message icon" className="icon" />
        <span className="__heading">{state.heading}</span>
        <span className="__description">{compileTemplateNewWithStyling(state.desc, { NAME })}</span>
        {shouldShowShareCTAs ? (
          <div className={`confirmation-cta-container ${!isTransactionFlow && 'marginBottom'}`}>
            <span>{state.shareMessage.confirmTitle}</span>
            <Popup
              className="share-message-copy-popup"
              hideOnScroll
              closeOnDocumentClick
              closeOnTriggerBlur
              trigger={
                <img
                  alt="copy details"
                  src={COPY_ICON}
                  className="details-copy-icon"
                  onClick={() => handleShareCTAClick(false)}
                />
              }
              on="click"
              content="Copied!"
            />
            <a
              href={`mailto:?subject=${state.shareMessage.emailSubject}&body=${handleShareCTAClick(
                true
              )}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              <img alt="email message" src={MAIL_ICON} className="details-copy-icon" />
            </a>
          </div>
        ) : null}
        {showConsent && (
          <div className={`__consentBox ${!shouldShowShareCTAs && '__noMarginTop'}`}>
            {state.transactionFlowLabel?.termsAndCondition}
          </div>
        )}
        <CommonButton
          //@ts-ignore
          text={isTransactionFlow ? state.transactionFlowLabel?.primaryCTA : state.primaryCtaText}
          onClick={primaryCtaOnClick}
          type="primary"
          width={'100%'}
        />
        {shouldShowSecondaryCTA && (
          <div className="__secondaryCTAContainer">
            <CommonButton
              //@ts-ignore
              text={
                isTransactionFlow
                  ? state.transactionFlowLabel?.secondaryCTA
                  : state.secondaryCtaText
              }
              onClick={secondaryCtaOnClick}
              type="link"
            />
          </div>
        )}
      </div>
    </ModalContainer>
  );
};
export default CustomInfoAlertMessage;
